// src/services/syncEngine.js
/**
 * Background sync engine: uploads queued offline recordings to the server
 * when the network is available. Reuses the existing /evaluate-pronunciation/
 * endpoint. At-least-once delivery — a local copy is deleted only after the
 * server confirms (HTTP 200). See OFFLINE_RECORDING_PLAN.md.
 */

import api from '../auth/api';
import {
  getPendingRecordings,
  deleteRecording,
  updateRecording,
  getPendingCount,
} from './offlineQueue';

const MAX_ATTEMPTS = 5;
const RETRY_INTERVAL_MS = 30000; // fallback poll every 30s

let isSyncing = false;
let intervalId = null;
let batchTotal = 0; // how many recordings the current sync run started with
const listeners = new Set();

/** Subscribe to sync status updates. Returns an unsubscribe fn. */
export function onSyncStatus(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

async function emit(extra = {}) {
  const pending = await getPendingCount();
  const status = { pending, syncing: isSyncing, total: batchTotal, ...extra };
  listeners.forEach((cb) => cb(status));
}

/** Try to upload all pending recordings. Safe to call repeatedly. */
export async function syncNow() {
  if (isSyncing || !navigator.onLine) return;
  isSyncing = true;
  await emit();

  try {
    const items = await getPendingRecordings();
    batchTotal = items.length; // remember the batch size so the UI can show "Syncing N…"
    await emit();
    for (const item of items) {
      if (!navigator.onLine) break; // lost network mid-sync; stop, retry later
      try {
        const submission = {
          child_id: item.child_id,
          exercise_id: item.exercise_id,
          audio_data: item.audio_data,
          reference_text: item.reference_text,
          hindi_word: item.hindi_word,
          language: item.language,
          // Undefined for practice items queued before capture existed, which
          // the server reads the same as null.
          capture_session_id: item.capture_session_id,
          client_recorded_at: item.client_recorded_at, // for server de-dup (optional)
        };
        await api.post('/evaluate-pronunciation/', submission);
        await deleteRecording(item.id); // confirmed → remove local copy
      } catch (err) {
        // Network/server error → bump attempts, keep for retry
        const attempts = (item.attempts || 0) + 1;
        await updateRecording({
          ...item,
          attempts,
          status: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
        });
        // Don't block the rest of the queue; move to next item
      }
      await emit();
    }
  } finally {
    isSyncing = false;
    batchTotal = 0;
    await emit({ justFinished: true });
  }
}

/** Start listening for connectivity + periodic retries. Call once on app load. */
export function startSyncEngine() {
  window.addEventListener('online', syncNow);
  if (!intervalId) {
    intervalId = setInterval(() => {
      if (navigator.onLine) syncNow();
    }, RETRY_INTERVAL_MS);
  }
  if (navigator.onLine) syncNow(); // catch up on startup
}

export function stopSyncEngine() {
  window.removeEventListener('online', syncNow);
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
}
